// Token Manager Component - Left panel for token management
import { EventManagerMixin } from '../utils/event-manager.js';
import { getCurrentTokenPosition, addTokenPositionHistoryEntry } from '../main.js';
import { CONFIG } from '../config.js';
import { HexEvents, createHexEvent } from '../utils/hex-events.js';
import { getTokenInitials } from '../utils/ui-helpers.js';

class TokenManager extends EventManagerMixin(HTMLElement) {
    constructor() {
        super();
        this.attachShadow({ mode: 'open' });
        this.tokens = [];
    }

    connectedCallback() {
        super.connectedCallback?.();
        this.render();
    }

    // disconnectedCallback is handled by EventManagerMixin - cleans up all listeners

    render() {
        this.shadowRoot.innerHTML = `
            <style>
                :host {
                    display: block;
                    width: 250px;
                    height: 100%;
                    background: var(--bg-surface, #1a1a1a);
                    border-right: 1px solid #333;
                    padding: 1rem;
                    overflow-y: auto;
                }
                h2 {
                    margin: 0 0 1rem 0;
                    font-size: 1.125rem;
                }
                .new-token-btn {
                    width: 100%;
                    background: var(--color-primary, #3b82f6);
                    color: white;
                    border: none;
                    padding: 0.75rem;
                    border-radius: 0.5rem;
                    cursor: pointer;
                    margin-bottom: 1rem;
                    font-size: 1rem;
                }
                .new-token-btn:hover {
                    opacity: 0.9;
                }
                .tokens-list {
                    display: flex;
                    flex-direction: column;
                    gap: 0.5rem;
                }
                .token-item {
                    background: var(--bg-elevated, #2a2a2a);
                    border: 1px solid #444;
                    border-radius: 0.5rem;
                    padding: 0.75rem;
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    cursor: move;
                    transition: transform 0.2s;
                }
                .token-item:hover {
                    transform: translateX(2px);
                    border-color: var(--color-primary, #3b82f6);
                }
                .token-avatar {
                    width: 40px;
                    height: 40px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: bold;
                    flex-shrink: 0;
                }
                .token-info {
                    flex: 1;
                }
                .token-name {
                    font-weight: bold;
                    margin-bottom: 0.25rem;
                }
                .token-status {
                    font-size: 0.75rem;
                    color: var(--text-secondary, #a0a0a0);
                }
                .token-actions {
                    display: flex;
                    gap: 0.25rem;
                }
                .token-action-btn {
                    background: none;
                    border: none;
                    color: var(--text-secondary, #a0a0a0);
                    cursor: pointer;
                    padding: 0.25rem;
                }
                .token-action-btn:hover {
                    color: var(--text-primary, #e5e5e5);
                }
                .empty-state {
                    text-align: center;
                    color: var(--text-secondary, ${CONFIG.COLORS.TEXT_MUTED});
                }
            </style>
            <h2>Tokens</h2>
            <button class="new-token-btn">+ New Token</button>
            <div class="tokens-list" id="tokens-list">
                ${this.renderTokens()}
            </div>
        `;

        // Clean up old listeners before setting up new ones (since render() can be called multiple times)
        this.cleanupListeners();
        this.setupEventListeners();
    }

    renderTokens() {
        if (this.tokens.length === 0) {
            return `<p class="empty-state">No tokens created yet</p>`;
        }

        return this.tokens.map(token => `
            <div class="token-item" draggable="true" data-token-id="${token.id}">
                <div class="token-avatar" style="background: ${token.color};">
                    ${token.imageUrl ?
                        `<img src="${token.imageUrl}" style="width: 100%; height: 100%; border-radius: 50%;">` :
                        token.initials
                    }
                </div>
                <div class="token-info">
                    <div class="token-name">${token.name}</div>
                    <div class="token-status">${(() => {
                        const position = getCurrentTokenPosition(token);
                        return position ? `Hex (${position.col}, ${position.row})` : 'Not placed';
                    })()}</div>
                </div>
                <div class="token-actions">
                    <button class="token-action-btn" title="Edit">✏️</button>
                    <button class="token-action-btn" title="Delete">🗑️</button>
                </div>
            </div>
        `).join('');
    }

    setupEventListeners() {
        const newTokenBtn = this.shadowRoot.querySelector('.new-token-btn');

        // Use EventManagerMixin's addManagedListener for automatic cleanup
        if (newTokenBtn) {
            this.addManagedListener(newTokenBtn, 'click', () => this.createNewToken());
        }

        // Setup drag events for tokens
        const shadowRoot = this.shadowRoot;
        this.addManagedListener(shadowRoot, 'dragstart', (e) => {
            if (e.target.classList.contains('token-item')) {
                e.dataTransfer.effectAllowed = 'move';
                e.dataTransfer.setData('text/plain', e.target.dataset.tokenId);
            }
        });

        // Handle Edit and Delete button clicks using event delegation
        this.addManagedListener(shadowRoot, 'click', (e) => {
            const editBtn = e.target.closest('button[title="Edit"]');
            const deleteBtn = e.target.closest('button[title="Delete"]');

            if (editBtn) {
                const tokenItem = editBtn.closest('.token-item');
                const tokenId = tokenItem?.dataset.tokenId;
                if (tokenId) {
                    this.editToken(tokenId);
                }
            } else if (deleteBtn) {
                const tokenItem = deleteBtn.closest('.token-item');
                const tokenId = tokenItem?.dataset.tokenId;
                if (tokenId) {
                    this.deleteToken(tokenId);
                }
            }
        });
    }

    createNewToken() {
        // Dispatch event to open token-creator dialog (handled in main.js)
        this.dispatchEvent(createHexEvent(HexEvents.CREATE_TOKEN));
    }

    editToken(tokenId) {
        const token = this.tokens.find(t => t.id === tokenId);
        if (token) {
            // Dispatch event to open token-creator dialog in edit mode
            this.dispatchEvent(createHexEvent(HexEvents.EDIT_TOKEN, { token }));
        }
    }

    deleteToken(tokenId) {
        // Dispatch event to delete token (handled in main.js)
        this.dispatchEvent(createHexEvent(HexEvents.TOKEN_DELETED, { tokenId }));
    }

    addToken(tokenData) {
        const token = {
            id: `token-${Date.now()}`,
            name: tokenData.name,
            color: tokenData.color || '#808080',
            imageUrl: tokenData.imageUrl || null,
            initials: getTokenInitials(tokenData.name),
            position: null
        };
        this.tokens.push(token);
        this.render();
        return token;
    }

    updateTokenPosition(tokenId, position) {
        const token = this.tokens.find(t => t.id === tokenId);
        if (token) {
            addTokenPositionHistoryEntry(token, position);
            this.render();
        }
    }

    removeToken(tokenId) {
        this.tokens = this.tokens.filter(t => t.id !== tokenId);
        this.render();
    }
}

customElements.define('token-manager', TokenManager);

export default TokenManager;